import { useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../redux/types/RootState'
import { store } from '../../redux/store'
import useStableCallback from './useStableCallback'
import { useEffectAfter } from './useEffectAfter'

interface TypedUseSelectorHookWithMidnightRefresh<TState> {
  <TSelected>(selector: (state: TState) => TSelected): TSelected
}
export const useSelectorWithMidnightRefresh: TypedUseSelectorHookWithMidnightRefresh<
  RootState
> = selector => {
  const stableSelector = useStableCallback(selector)
  const selectorValue = useSelector(stableSelector)
  const [value, setValue] = useState(selectorValue)
  const [trigger, setTrigger] = useState(1)
  const timeoutRef = useRef<NodeJS.Timeout>()
  const stableScheduleTrigger = useStableCallback(() => {
    function scheduleTrigger() {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      const now = new Date()
      const nextMidnight = new Date(now)
      nextMidnight.setHours(24, 0, 0, 0) // move to today's midnight + 1 day
      const msUntilMidnight = nextMidnight.getTime() - now.getTime()
      timeoutRef.current = setTimeout(() => {
        setTrigger(trigger => trigger + 1)
        scheduleTrigger()
      }, msUntilMidnight)
    }
    scheduleTrigger()
  })
  useEffectAfter(
    () => {
      setValue(stableSelector(store.getState()))
    },
    [stableSelector, trigger, selectorValue],
    1,
  )
  useEffect(() => {
    stableScheduleTrigger()
    return () => {
      clearTimeout(timeoutRef.current)
    }
  }, [stableScheduleTrigger])
  return value
}
